import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";

import type { TemplateSummary } from "../../api/types";
import { configIncompleteDetails, websiteService } from "../../api/websiteService";
import { NO_NEWS_LIST_HINT, TemplateCapabilityChips } from "./TemplateCapabilities";

/** 弹窗要的模板信息:code / name 必有;能力字段有就标(1009 C 线:让客户看清这套模板能干什么)。 */
export type DialogTemplate = Pick<TemplateSummary, "code" | "name"> &
  Partial<Pick<TemplateSummary, "hostTemplate" | "dynamic" | "articleList">>;

const schema = z.object({
  name: z.string().trim().min(1, "给站点起个名字").max(80, "名字别超过 80 个字"),
});
type Values = z.infer<typeof schema>;

/**
 * 「用这套建站」弹窗:只问一个站点名 → `POST /sites {name, templateCode}` → 跳「生成」页(1010 三段式第一段)。
 *
 * ★ 写口不回桩(dev 下也直接抛):建站失败就是失败,toast 说清是哪一种 ——
 *   422 CONFIG_INCOMPLETE 的 `details` 整批列出(0830 教训:只亮第一条,修掉一个揭开下一个);
 *   403 是模块没开通;其余按 message。
 */
export function CreateSiteDialog({
  open,
  onOpenChange,
  template,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: DialogTemplate | null;
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: "" } });

  // 每次打开都是一张白表,不把上一次没提交的名字带进来。
  useEffect(() => {
    if (open) form.reset({ name: "" });
  }, [open, form]);

  const submit = async (values: Values) => {
    if (!template) return;
    setSubmitting(true);
    try {
      const site = await websiteService.createSite({
        name: values.name,
        templateCode: template.code,
      });
      onOpenChange(false);
      navigate(`/ai-website/sites/${site.id}/generate`);
    } catch (e) {
      const details = configIncompleteDetails(e);
      const message = e instanceof Error ? e.message : String(e);
      const status = (e as { httpStatus?: unknown } | null)?.httpStatus;
      if (details) {
        toast({
          title: "建站舱还没配好",
          description: details.length ? details.join("\n") : message,
          variant: "destructive",
        });
      } else if (status === 403) {
        toast({ title: "建站舱未开通", description: message, variant: "destructive" });
      } else {
        toast({ title: "建站失败", description: message, variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="normal-case tracking-normal">新建站点</DialogTitle>
          <DialogDescription>
            {template ? (
              <>
                模板:{template.name}
                {/* 宿主模板的 code 是 uuid,对客没意义,不露 */}
                {!template.hostTemplate && (
                  <span className="ml-1 font-mono text-2xs">{template.code}</span>
                )}
                。建好后自动读取知识库、产品库、品牌资产,一键生成整站并先发到免费子域名预览。
              </>
            ) : (
              "先在左边挑一套模板。"
            )}
          </DialogDescription>
        </DialogHeader>
        {template && (template.dynamic || template.articleList === true) && (
          <ul className="flex flex-wrap gap-1.5 text-2xs" aria-label="这套模板能做">
            <TemplateCapabilityChips
              template={{ dynamic: !!template.dynamic, articleList: template.articleList }}
            />
          </ul>
        )}
        {template?.hostTemplate && template.articleList === false && (
          <p role="note" className="text-2xs leading-relaxed text-muted-foreground">
            {NO_NEWS_LIST_HINT}
          </p>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>站点名称</FormLabel>
                  <FormControl>
                    <Input maxLength={80} placeholder="站点在列表里叫什么" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit" disabled={submitting || !template}>
                {submitting && <Loader2 className="animate-spin" />}
                建站并开始生成
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
